import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getOrder, deleteOrder, updateOrderStatus } from '../api';
import ConfirmDialog from '../components/ConfirmDialog';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { fmtCurrency } from '../utils/formatCurrency';
import EmptyValue from '../components/EmptyValue';
import { useToast } from '../context/ToastContext';
import useDetailBreadcrumbs from '../hooks/useDetailBreadcrumbs';

function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, canDo, isModuleVisible } = useAuth();
  const toast = useToast();
  const [order, setOrder] = useState(null);
  useDetailBreadcrumbs(order ? [{ label: 'Orders', path: '/orders' }, { label: order.order_number || `Order #${order.id}` }] : null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    try {
      const response = await getOrder(id);
      setOrder(response.data);
    } catch (err) { setError('Failed to load order'); }
    finally { setLoading(false); }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const response = await updateOrderStatus(id, newStatus);
      if (response.data?.opportunity_stage_updated) {
        toast.info('Opportunity automatically moved to Closed Won');
      }
      loadData();
    } catch (err) { setError(err.response?.data?.error || 'Failed to update status'); }
  };

  const handleDelete = async () => {
    try {
      await deleteOrder(id);
      navigate('/orders');
    } catch (err) { setError(err.response?.data?.error || 'Failed to delete order'); }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Order Created': return 'warning';
      case 'Shipped': return 'success';
      default: return 'gray';
    }
  };

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;
  if (!order) return <div className="alert alert-error">Order not found</div>;

  // All fields are locked for orders created from quotes
  const isFromQuote = !!order.quote_id;

  return (
    <div className="order-detail">
      <div className="page-header">
        <div className="flex items-center gap-4">
          <button className="btn btn-icon" onClick={() => navigate(location.state?.fromQuote ? `/quotes/${location.state.fromQuote}` : location.state?.fromAccount ? `/accounts/${location.state.fromAccount}` : '/orders')} aria-label="Go back"><ArrowLeft size={20} /></button>
          <div>
            <h1 className="page-title">{order.order_number}</h1>
            <span className={`badge badge-${getStatusColor(order.status)}`}>{order.status}</span>
          </div>
        </div>
        <div className="page-actions">
          {canDo('orders', 'edit') && order.status !== 'Shipped' ? (
            <select className="form-select filter-dropdown-sm" value={order.status} onChange={(e) => handleStatusChange(e.target.value)} aria-label="Change order status">
              <option value="Order Created">Order Created</option>
              <option value="Shipped">Shipped</option>
            </select>
          ) : (
            <span className={`badge badge-${getStatusColor(order.status)}`}>{order.status}</span>
          )}
          {canDo('orders', 'delete') && <button className="btn btn-danger" onClick={() => setShowDeleteConfirm(true)} aria-label="Delete order"><Trash2 size={18} /></button>}
        </div>
      </div>

      {error && <div className="alert alert-error" role="alert" aria-live="assertive">{error}</div>}

      <div className="detail-layout detail-layout-3col">
        <div className="card">
          <div className="card-header"><h3 className="card-title">Line Items</h3></div>
          <div className="table-container">
            <table className="table">
              <thead><tr><th scope="col">Part #</th><th scope="col">Product</th><th scope="col">Description</th><th scope="col">Qty</th><th scope="col">Unit Price</th><th scope="col">Total</th></tr></thead>
              <tbody>
                {order.line_items?.map((item, i) => (
                  <tr key={i}><td>{item.product_sku || item.sku || <EmptyValue label="No SKU" />}</td><td>{item.product_name}</td><td>{item.description || <EmptyValue label="No description" />}</td><td>{item.quantity}</td><td>${fmtCurrency(item.unit_price)}</td><td>${fmtCurrency(item.line_total)}</td></tr>
                ))}
              </tbody>
              <tfoot>
                <tr><td colSpan="5" className="text-right font-semibold">Total</td><td className="font-semibold">${fmtCurrency(order.total)}</td></tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3 className="card-title">Details</h3></div>
          <div className="card-body">
            <div className="detail-row"><span className="detail-label">Account</span><span className="detail-value">{isModuleVisible('accounts') ? <Link to={`/accounts/${order.account_id}`}>{order.account_name}</Link> : <span>{order.account_name}</span>}</span></div>
            <div className="detail-row"><span className="detail-label">Order Date</span><span className="detail-value">{new Date(order.order_date).toLocaleDateString()}</span></div>
            {order.quote_id && <div className="detail-row"><span className="detail-label">From Quote</span><span className="detail-value">{isModuleVisible('quotes') ? <Link to={`/quotes/${order.quote_id}`}>{order.quote_number}</Link> : <span>{order.quote_number}</span>}</span></div>}
            {order.opportunity_name && <div className="detail-row"><span className="detail-label">Opportunity</span><span className="detail-value">{isModuleVisible('opportunities') ? <Link to={`/opportunities/${order.opportunity_id}`}>{order.opportunity_name}</Link> : <span>{order.opportunity_name}</span>}</span></div>}
            <div className="detail-row"><span className="detail-label">Created By</span><span className="detail-value">{order.created_by_name}</span></div>
            {order.notes && <div className="detail-row"><span className="detail-label">Notes</span><span className="detail-value">{order.notes}</span></div>}
            {isFromQuote && (
              <div style={{ marginTop: '12px', padding: '10px 14px', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', fontSize: '13px', color: 'var(--text-secondary)' }}>
                All fields are inherited from the linked quote and cannot be edited.
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} onConfirm={handleDelete} title="Delete Order" message="Delete this order? This cannot be undone." confirmText="Delete" />
    </div>
  );
}

export default OrderDetail;
